import { z } from 'zod';
import { apiUrl } from '../config/api';
import type { Project } from '../data/databaseSchema';
import { apiClient } from '../lib/apiClient';
import { envelopeSchema, projectSchema } from '../lib/schemas';

export interface ProjectOptionItem {
  id: string;
  display: string;
}

const optionItemSchema = z.object({ id: z.string(), display: z.string() });

export const projectService = {
  async getProjects(): Promise<Project[]> {
    const result = await apiClient.get(apiUrl.projects(), {
      validate: envelopeSchema('projects', z.array(projectSchema)),
      deduplicate: true,
      context: 'getProjects',
    });
    return result.projects;
  },

  async getProjectsByRole(): Promise<Project[]> {
    const result = await apiClient.get(apiUrl.projectsByRole(), {
      validate: envelopeSchema('projects', z.array(projectSchema)),
      deduplicate: true,
      context: 'getProjectsByRole',
    });
    return result.projects;
  },

  async createProject(project: Partial<Project>): Promise<Project> {
    const result = await apiClient.post(apiUrl.projects(), project, {
      validate: envelopeSchema('project', projectSchema),
      context: 'createProject',
    });
    return result.project;
  },

  async updateProject(id: string, project: Partial<Project>): Promise<Project> {
    const result = await apiClient.put(apiUrl.projects(id), project, {
      validate: envelopeSchema('project', projectSchema),
      context: 'updateProject',
    });
    return result.project;
  },

  async deleteProject(id: string): Promise<void> {
    await apiClient.delete(apiUrl.projects(id), {
      context: 'deleteProject',
    });
  },

  async archiveProject(id: string): Promise<void> {
    await apiClient.patch(`${apiUrl.projects(id)}/archive`, undefined, {
      context: 'archiveProject',
    });
  },

  async restoreProject(id: string): Promise<void> {
    await apiClient.patch(`${apiUrl.projects(id)}/restore`, undefined, {
      context: 'restoreProject',
    });
  },

  async getPortfolios(): Promise<ProjectOptionItem[]> {
    const result = await apiClient.get(apiUrl.projectOptionsPortfolios(), {
      validate: envelopeSchema('portfolios', z.array(optionItemSchema)),
      context: 'getPortfolios',
    });
    return result.portfolios;
  },

  async getProcessTechnologies(): Promise<ProjectOptionItem[]> {
    const result = await apiClient.get(apiUrl.projectOptionsProcessTechnologies(), {
      validate: envelopeSchema('process_technologies', z.array(optionItemSchema)),
      context: 'getProcessTechnologies',
    });
    return result.process_technologies;
  },

  async getDeliveryModels(): Promise<ProjectOptionItem[]> {
    const result = await apiClient.get(apiUrl.projectOptionsDeliveryModels(), {
      validate: envelopeSchema('delivery_models', z.array(optionItemSchema)),
      context: 'getDeliveryModels',
    });
    return result.delivery_models;
  },
};
